import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import WaterLedgerLogo from "@components/icons/WaterLedgerLogo";
import Modal from "@components/layout/Modal";
import Tag from "@components/shared/Tag";
import { UploadRow } from "@context/subscriberSyncUpload/reducer";
import { RowDecisionInput } from "@services/subscriberSyncUpload";
import { STATUS_TAG } from "./rowDisplay";

// Read-only File-vs-Ledger comparison, mirroring the bulk-meter "Verified by Water Ledger" modal.
// The CRM/file values come from row.rowData; the current SEQWL/ledger values from row.seqwlSnapshot
// (null for not_found / invalid). Changed fields (row.diffs) are highlighted on both sides. Editing
// is not done here — approve/decline live in the footer; per-row notes are edited inline in the grid.
type RowDetailModalProps = {
  row: UploadRow;
  onClose: () => void;
  onDecide: (decision: RowDecisionInput) => void;
  deciding: boolean;
};

type FieldRow = {
  key: string;
  label: string;
  file: React.ReactNode;
  ledger: React.ReactNode;
};

const RowDetailModal: React.FunctionComponent<RowDetailModalProps> = ({
  row,
  onClose,
  onDecide,
  deciding,
}) => {
  const { t } = useTranslation();

  const rowData = (row.rowData ?? {}) as Record<string, unknown>;
  const snap = row.seqwlSnapshot ?? null;
  const sub = snap?.subscriber;
  const usr = snap?.user;
  const addr = sub?.address;

  const fmt = (v: unknown): string =>
    v === undefined || v === null || v === "" ? "—" : String(v);
  const fmtActive = (v: unknown): string =>
    v === true ? t("common.yes") : v === false ? t("common.no") : "—";

  const label = (k: string) => t(`subscriber_sync_upload.modal.field.${k}`);

  // Account number only has a ledger-side value when a customer was matched.
  const ledgerAccount = row.matchedSubscriberId ? rowData.accountNumber : undefined;

  const fields: FieldRow[] = [
    { key: "accountNumber", label: label("account_number"), file: fmt(rowData.accountNumber), ledger: fmt(ledgerAccount) },
    { key: "name", label: label("name"), file: fmt(rowData.name), ledger: fmt(sub?.name) },
    { key: "abn", label: label("abn"), file: fmt(rowData.abn), ledger: fmt(sub?.abn) },
    { key: "acn", label: label("acn"), file: fmt(rowData.acn), ledger: fmt(sub?.acn) },
    { key: "email", label: label("email"), file: fmt(rowData.email), ledger: fmt(usr?.email) },
    { key: "mobile", label: label("mobile"), file: fmt(rowData.mobile), ledger: fmt(usr?.tel) },
    { key: "street", label: label("street"), file: fmt(rowData.street), ledger: fmt(addr?.street) },
    { key: "town", label: label("town"), file: fmt(rowData.town), ledger: fmt(addr?.town) },
    { key: "region", label: label("region"), file: fmt(rowData.region), ledger: fmt(addr?.region) },
    { key: "postcode", label: label("postcode"), file: fmt(rowData.postcode), ledger: fmt(addr?.postcode) },
    { key: "country", label: label("country"), file: fmt(rowData.country), ledger: fmt(addr?.country) },
    { key: "isActive", label: label("is_active"), file: fmtActive(rowData.isActive), ledger: fmtActive(sub?.isActive) },
  ];

  // Field keys that differ — used to highlight both columns. diff field names are flat and match the
  // keys above (e.g. 'street', 'isActive').
  const changed = new Set<string>(row.diffs.map(d => String(d.field)));
  const ledgered = row.status === 1; // SubscriberSyncUploadRowStatus.Success — committed, read-only
  const statusTag = STATUS_TAG[row.rowStatus];

  // AC-24: the date a field's current SEQWL value was last changed by the wallet user via Zenith
  // self-service (per field, including email/mobile), or null. Only shown for fields that differ,
  // beside the current (ledger) value, so the operator can spot a recent customer change before
  // overwriting it.
  const selfService = snap?.selfService ?? null;
  const selfServiceDate = (key: string): string | null => {
    const iso = selfService?.fields?.[key];
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
  };

  const renderColumn = (
    titleKey: string,
    pick: (f: FieldRow) => React.ReactNode,
    withSelfService = false,
    // Diff highlighting + self-service badges only make sense pre-commit. A ledgered row renders a
    // single, plain column of the applied values (highlight = false), not a File-vs-Ledger comparison.
    highlight = true,
  ) => (
    <div className={ledgered ? "w-full" : "w-1/2"}>
      <h4 className="font-semibold py-2 px-4 bg-gray-100 mb-4">{t(titleKey)}</h4>
      <ul className="text-gray-600 text-sm space-y-1">
        {fields.map(f => {
          const isChanged = highlight && changed.has(f.key);
          const ssDate = withSelfService && changed.has(f.key) ? selfServiceDate(f.key) : null;
          return (
            <li key={f.key} className={classNames({ "text-yellow-600": isChanged })}>
              <strong className="text-gray-700">{f.label}</strong> {pick(f)}
              {ssDate ? (
                <span
                  className="block text-xs text-blue-600"
                  data-testid={`self-service-${f.key}`}
                >
                  {t("subscriber_sync_upload.modal.zenith_self_service", { date: ssDate })}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <Modal open className="sm:max-w-3xl">
      <header className="p-6 flex gap-2 border-b items-center">
        <WaterLedgerLogo />
        <h3 className="text-xl">{t("audit_trail.modal_title")}</h3>
        <div className="ml-auto">
          <Tag status={ledgered ? "success" : statusTag.status}>
            {ledgered ? t("subscriber_sync_upload.row_status.ledgered") : t(statusTag.key)}
          </Tag>
        </div>
      </header>

      <div className="p-6 border-b space-y-3">
        {ledgered ? (
          <p>{t("subscriber_sync_upload.modal.ledgered_explanation")}</p>
        ) : null}
        {!ledgered && row.rowStatus === "has_differences" ? (
          <p>{t("subscriber_sync_upload.modal.diff_summary", { count: row.diffs.length })}</p>
        ) : null}
        {!ledgered && row.rowStatus === "in_sync" ? (
          <p>{t("subscriber_sync_upload.modal.in_sync_explanation")}</p>
        ) : null}
        {!ledgered && row.rowStatus === "not_found" ? (
          <p>{t("subscriber_sync_upload.modal.not_found_explanation")}</p>
        ) : null}
        {!ledgered && row.rowStatus === "invalid" ? (
          <>
            <p>{t("subscriber_sync_upload.modal.invalid_explanation")}</p>
            <ul className="list-inside list-disc space-y-1 text-red-600 text-sm">
              {row.formatErrors.map((err, i) => (
                <li key={`${err.code}-${i}`}>
                  {t(`subscriber_sync_upload.modal.error.${err.code}`, {
                    value: err.field ? (rowData[err.field] as string) ?? "" : "",
                  })}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <div className="flex p-6 border-b gap-4">
        {ledgered ? (
          // Read-only record: the applied values are what now lives in the ledger, so show a single
          // plain column (no File-vs-Ledger comparison, no diff highlight).
          renderColumn("subscriber_sync_upload.modal.ledger_details", f => f.file, false, false)
        ) : (
          <>
            {renderColumn("subscriber_sync_upload.modal.file_details", f => f.file)}
            {renderColumn("subscriber_sync_upload.modal.ledger_details", f => f.ledger, true)}
          </>
        )}
      </div>

      <footer className="px-6 py-4 flex justify-end gap-2">
        {/* Already-ledgered rows (status === Success) are read-only — no re-approve/decline. */}
        {row.rowStatus === "has_differences" && row.status !== 1 ? (
          <>
            <button
              type="button"
              className="btn-primary text-sm rounded"
              disabled={deciding}
              onClick={() => onDecide("approved")}
            >
              {t("common.approve")}
            </button>
            <button
              type="button"
              className="btn-outline-primary text-sm rounded"
              disabled={deciding}
              onClick={() => onDecide("declined")}
            >
              {t("subscriber_sync_upload.decline")}
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="btn-outline-primary text-sm rounded"
          onClick={onClose}
        >
          {t("common.close")}
        </button>
      </footer>
    </Modal>
  );
};

export default RowDetailModal;
